/**
 * Per-object localStorage autosave for whiteboard content (Anke, 2026-09-16:
 * "ungespeicherte Änderungen sollen nicht verloren gehen, bitte im Browser
 * speichern"). Mirrors `@o2d/rich-text-editor`'s own `DraftStore`
 * (`packages/rich-text-editor/src/controllers/draft-store.ts`) — same
 * debounced-save / saveNow / load / clear / cancelPending shape — but lives
 * here in horstplan rather than in `@o2d/whiteboard`: the wrapper component
 * already receives the full `objects` array on every `whiteboard-changed`
 * event (`_onChanged`), so there is no need to reach inside the package's
 * own internal state the way the RTE's tiptap-driven autosave does.
 *
 * Key format is a horstplan-owned convention, distinct from the RTE
 * package's `o2d.rte-draft.<uid>` prefix — see
 * `knowledge-draft-registry.helper.ts` for the existence check that looks at
 * BOTH prefixes for the sidebar's unsaved-indicator.
 *
 * The key is `o2d.whiteboard-draft.<uid>.<userUid>` (review 2026-09-19, F13/G4):
 * localStorage is shared by everyone using the same browser and a session
 * expiry does not clear it. With an owner only INSIDE the value, a second user
 * could not apply a stranger's draft, but writing, saving or cancelling would
 * still overwrite or delete it under the same key — data loss for the real
 * owner. With the owner in the key each user's draft is untouchable by
 * anybody else. An unknown current user (`''`) owns nothing: every function
 * here is then a no-op (write, clear, cancel) or `undefined` (read).
 * The key is only unambiguous while neither `uid` nor `userUid` contains a
 * `.` (`a` + `b.c` would collide with `a.b` + `c`): both are base64url ShortIds
 * (letters, digits, `-`, `_`), which holds today.
 *
 * Each entry carries `baselineUpdatedAt` — the KnowledgeObject's own
 * `updatedAt` (ISO string) at the moment this draft was last written
 * against (Anke, 2026-09-17 — the randfall she flagged: reacquiring an
 * abandoned lock, e.g. after a hard tab close, blindly re-applied whatever
 * local draft existed, even if someone else had since taken the lock over
 * AND saved a newer version — silently clobbering it with the stale local
 * content). `knowledge-whiteboard.component.ts`'s `_restoreDraftIfEditing`
 * compares this against the freshly-loaded object's real `updatedAt` before
 * auto-applying a draft, and asks instead of silently overwriting when they
 * disagree.
 */

interface WhiteboardDraftEntry {
    readonly objects: readonly unknown[];
    readonly baselineUpdatedAt: string;
}

const KEY_PREFIX = "o2d.whiteboard-draft.";
const DEBOUNCE_MS = 500;

const pendingTimers = new Map<string, ReturnType<typeof setTimeout>>();

function keyOf(uid: string, userUid: string): string {
    return `${KEY_PREFIX}${uid}.${userUid}`;
}

/** The pre-G4 key without an owner suffix — unusable for everyone, only ever removed. */
function legacyKeyOf(uid: string): string {
    return `${KEY_PREFIX}${uid}`;
}

function timerKeyOf(uid: string, userUid: string): string {
    return `${uid}.${userUid}`;
}

function parseEntry(raw: string): WhiteboardDraftEntry | undefined {
    try {
        const parsed = JSON.parse(raw) as Partial<WhiteboardDraftEntry> | null;
        if (parsed === null || !Array.isArray(parsed.objects) || typeof parsed.baselineUpdatedAt !== 'string') {
            return undefined;
        }
        return parsed as WhiteboardDraftEntry;
    } catch {
        return undefined;
    }
}

/** Debounced write — the last call within `DEBOUNCE_MS` wins. */
export function saveWhiteboardDraft(uid: string, objects: readonly unknown[], baselineUpdatedAt: string, userUid: string): void {
    if (userUid === '') {
        return;
    }
    cancelPendingWhiteboardDraft(uid, userUid);
    pendingTimers.set(timerKeyOf(uid, userUid), setTimeout(() => {
        pendingTimers.delete(timerKeyOf(uid, userUid));
        saveWhiteboardDraftNow(uid, objects, baselineUpdatedAt, userUid);
    }, DEBOUNCE_MS));
}

/** Synchronous write, bypassing the debounce — cancels any pending timer first. */
export function saveWhiteboardDraftNow(uid: string, objects: readonly unknown[], baselineUpdatedAt: string, userUid: string): void {
    if (userUid === '') {
        return;
    }
    cancelPendingWhiteboardDraft(uid, userUid);
    try {
        const entry: WhiteboardDraftEntry = {objects, baselineUpdatedAt};
        localStorage.setItem(keyOf(uid, userUid), JSON.stringify(entry));
    } catch (error) {
        console.error(`Failed to autosave the whiteboard draft for «${uid}».`, error);
    }
}

/**
 * Non-destructive presence check for the sidebar's render path: is there a
 * USABLE draft of this user? Never writes or removes anything — cleaning up
 * unusable entries is `loadWhiteboardDraft`'s job, at load time.
 */
export function peekWhiteboardDraft(uid: string, userUid: string): boolean {
    if (userUid === '') {
        return false;
    }
    try {
        const raw = localStorage.getItem(keyOf(uid, userUid));
        return raw !== null && parseEntry(raw) !== undefined;
    } catch {
        return false;
    }
}

/**
 * Returns the current user's own draft, or `undefined` — never throws.
 *
 * An unusable entry of the current user (corrupt JSON, wrong shape) and the
 * owner-less legacy key of this uid are removed here: nothing else would ever
 * clean them up (review 2026-09-19, F7). Other users' entries live under
 * other keys and are never touched.
 */
export function loadWhiteboardDraft(uid: string, userUid: string): WhiteboardDraftEntry | undefined {
    if (userUid === '') {
        return undefined;
    }
    try {
        localStorage.removeItem(legacyKeyOf(uid));
        const raw = localStorage.getItem(keyOf(uid, userUid));
        if (raw === null) {
            return undefined;
        }
        const entry = parseEntry(raw);
        if (entry === undefined) {
            localStorage.removeItem(keyOf(uid, userUid));
        }
        return entry;
    } catch {
        return undefined;
    }
}

export function clearWhiteboardDraft(uid: string, userUid: string): void {
    if (userUid === '') {
        return;
    }
    cancelPendingWhiteboardDraft(uid, userUid);
    try {
        localStorage.removeItem(keyOf(uid, userUid));
    } catch {
        // Best-effort — a failed removal leaves a stale draft behind, which
        // is no worse than never having cleared it.
    }
}

export function cancelPendingWhiteboardDraft(uid: string, userUid: string): void {
    const key = timerKeyOf(uid, userUid);
    const existing = pendingTimers.get(key);
    if (existing) {
        clearTimeout(existing);
        pendingTimers.delete(key);
    }
}
